/**
 * Permissive timeseries-ref extractor — accepts every shape a row column
 * can carry across the pipeline:
 *
 * - **Bare externalId string** — CDF flattens timeseries-typed instanceId
 *   properties to a string at the source.
 * - **Instance reference object** (`{space, externalId}`) — CDF returns
 *   entity-typed instanceId properties in this shape.
 * - **{@link EnrichedTimeseriesRef}** — produced by
 *   {@link enrichTimeseriesRefs} after a query-knowledge-graph step. Has
 *   `externalId` directly, with `space` optional.
 *
 * Returns the canonical `{externalId, space?}` shape, or `null` for any
 * other input. Single source of truth for "is this column value a
 * timeseries reference, and what's its externalId?" — used by both
 * enrichment (collects externalIds for the initial datapoints fetch) and
 * derive-time prefetch (looks up the externalId in the prefetched map).
 */
export function extractTsRef(value: unknown): { externalId: string; space?: string } | null {
  if (typeof value === 'string') {
    return { externalId: value };
  }
  if (value === null || typeof value !== 'object') {
    return null;
  }
  const obj = value as { externalId?: unknown; space?: unknown };
  if (typeof obj.externalId !== 'string') {
    return null;
  }
  if (typeof obj.space === 'string') {
    return { externalId: obj.externalId, space: obj.space };
  }
  return { externalId: obj.externalId };
}
